import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import cx from "classnames";
import { type FC, useCallback, useEffect, useRef } from "react";
import { Badge, Form, Nav } from "react-bootstrap";
import {
  NavLink,
  Outlet,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { Icon } from "src/components/fragments";
import Title from "src/components/title";
import { useSearchAll } from "src/graphql";
import { useDebouncedCallback } from "src/hooks";

const CLASSNAME = "SearchPage";
const CLASSNAME_INPUT = `${CLASSNAME}-input`;

export const SearchLayout: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const term = searchParams.get("q") ?? "";
  const query = term ? `?q=${encodeURIComponent(term)}` : "";

  const inputRef = useRef<HTMLInputElement>(null);
  const inputValueRef = useRef(term);

  useEffect(() => {
    if (inputRef.current && term !== inputValueRef.current) {
      inputRef.current.value = term;
      inputValueRef.current = term;
    }
  }, [term]);

  const debouncedSearch = useDebouncedCallback(
    (searchTerm: string, pathname: string) => {
      const q = searchTerm ? `?q=${encodeURIComponent(searchTerm)}` : "";
      navigate(`${pathname}${q}`, { replace: true });
    },
    200,
  );

  const handleSearch = useCallback(
    (searchTerm: string) => {
      inputValueRef.current = searchTerm;
      debouncedSearch(searchTerm, location.pathname);
    },
    [debouncedSearch],
  );

  const { data: searchData } = useSearchAll({ term, limit: 10 }, !term);

  const performerCount = searchData?.searchPerformers.count;
  const sceneCount = searchData?.searchScenes.count;

  return (
    <div className={CLASSNAME}>
      <Title page={term || "Search"} />
      <Form.Group className={cx(CLASSNAME_INPUT, "mb-3")}>
        <Icon icon={faMagnifyingGlass} />
        <Form.Control
          ref={inputRef}
          defaultValue={term}
          onChange={(e) => handleSearch(e.currentTarget.value)}
          placeholder="Search for performer or scene"
          autoFocus
        />
      </Form.Group>

      <Nav variant="tabs" className="mb-3">
        <Nav.Item>
          <Nav.Link as={NavLink} to={`/search${query}`} end>
            All
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={NavLink} to={`/search/performers${query}`}>
            Performers
            {performerCount !== undefined && (
              <Badge bg="secondary" className="ms-2">
                {performerCount}
              </Badge>
            )}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={NavLink} to={`/search/scenes${query}`}>
            Scenes
            {sceneCount !== undefined && (
              <Badge bg="secondary" className="ms-2">
                {sceneCount}
              </Badge>
            )}
          </Nav.Link>
        </Nav.Item>
      </Nav>

      <Outlet />
    </div>
  );
};
